import { useCallback, useRef, useState } from 'react';

/**
 * Plays a sponsor's video before the content starts.
 *
 * <h2>Why this replaced Google IMA</h2>
 *
 * Sponsor ads used to run through Google's IMA SDK: the sponsor's MP4 URL was wrapped
 * in an inline VAST document and handed to a 688-line service that loaded Google's
 * player from imasdk.googleapis.com.
 *
 * That is an enormous amount of machinery for "play this file, then play the content".
 * VAST exists so that an ad SERVER can decide what to show at request time, negotiate
 * with bidders and report impressions back. StrymHub does direct deals - a partner
 * sends a video and an agreed run - so there is no server to negotiate with and
 * nothing to parse.
 *
 * Dropping it also removes a third-party script that loaded on every player page,
 * which was a data flow to Google to disclose and consent-gate, and exposure under the
 * Age Appropriate Design Code given under-18s can register.
 *
 * <h2>What this deliberately does not do</h2>
 *
 * No VAST, no ad server, no impression beacons, no bidding. If a partner ever supplies
 * a VAST tag rather than a file, that needs a VAST player and this is not it - which is
 * a better place to be than carrying one permanently on the chance that they might.
 */

export type SponsorSlot = 'preroll';

export interface SponsorCreative {
  videoUrl: string;
  /** Where clicking the ad takes the viewer. Optional: not every sponsor wants a link. */
  clickThroughUrl?: string;
  /** Seconds before Skip appears. 0 means the viewer may skip immediately. */
  skipAfterSeconds: number;
}

/**
 * Reads the configured creative for a slot.
 *
 * Returns null when nothing is configured, which is the normal state: there is no
 * sponsor most of the time, and the player must behave exactly as if this hook did not
 * exist.
 */
export function getSponsorCreative(slot: SponsorSlot): SponsorCreative | null {
  const env = import.meta.env;
  const prefix = `VITE_SPONSOR_${slot.toUpperCase()}`;

  const videoUrl = (env[`${prefix}_MP4_URL` as keyof ImportMetaEnv] as string) || '';
  if (!videoUrl) return null;

  const skipRaw = (env[`${prefix}_SKIP_OFFSET` as keyof ImportMetaEnv] as string) || '5';
  const skipAfterSeconds = Number.parseInt(skipRaw, 10);

  return {
    videoUrl,
    clickThroughUrl: (env[`${prefix}_CLICKTHROUGH_URL` as keyof ImportMetaEnv] as string) || undefined,
    // A non-numeric or negative value must not mean "never skippable" - an ad a
    // viewer cannot escape because of a typo in configuration is a worse outcome
    // than one they can skip immediately.
    skipAfterSeconds: Number.isFinite(skipAfterSeconds) && skipAfterSeconds >= 0 ? skipAfterSeconds : 5,
  };
}

interface Options {
  /** False disables the slot entirely, e.g. for previews or where a sponsor would be inappropriate. */
  enabled: boolean;
  /** Runs when the sponsor video finishes or is skipped, so the player can start the content. */
  onFinished: () => void;
}

export function useSponsorPreroll({ enabled, onFinished }: Options) {
  const creative = enabled ? getSponsorCreative('preroll') : null;

  const sponsorVideoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [canSkip, setCanSkip] = useState(false);
  const [secondsUntilSkip, setSecondsUntilSkip] = useState(0);

  // Once per page view. Without this, every pause and resume of the content would
  // replay the sponsor, which is the fastest way to make a viewer leave.
  const hasPlayedRef = useRef(false);

  const finish = useCallback(() => {
    setIsPlaying(false);
    setCanSkip(false);
    onFinished();
  }, [onFinished]);

  /**
   * Call before starting the content. Returns true if a sponsor video took over, so
   * the caller knows not to start playback itself.
   */
  const playIfPresent = useCallback((): boolean => {
    if (!creative || hasPlayedRef.current) return false;

    hasPlayedRef.current = true;
    setIsPlaying(true);
    setCanSkip(creative.skipAfterSeconds === 0);
    setSecondsUntilSkip(creative.skipAfterSeconds);

    // Play failures are common and mundane - autoplay policies, a bad URL, an offline
    // CDN. None of them justify withholding the content the viewer actually came for,
    // so any failure goes straight to finish().
    sponsorVideoRef.current?.play().catch(() => finish());
    return true;
  }, [creative, finish]);

  const handleTimeUpdate = useCallback(() => {
    if (!creative) return;
    const elapsed = sponsorVideoRef.current?.currentTime ?? 0;
    const remaining = Math.max(0, Math.ceil(creative.skipAfterSeconds - elapsed));
    setSecondsUntilSkip(remaining);
    if (remaining === 0) setCanSkip(true);
  }, [creative]);

  const skip = useCallback(() => {
    sponsorVideoRef.current?.pause();
    finish();
  }, [finish]);

  return {
    /** Null when no sponsor is configured - the overwhelmingly common case. */
    creative,
    sponsorVideoRef,
    isSponsorPlaying: isPlaying,
    canSkip,
    secondsUntilSkip,
    playIfPresent,
    handleSponsorTimeUpdate: handleTimeUpdate,
    handleSponsorEnded: finish,
    /** Same treatment as a play failure: never trap the viewer behind a broken ad. */
    handleSponsorError: finish,
    skipSponsor: skip,
  };
}
